import { useContext, useState } from "react";
import CredentialsHeader from "@/components/header/Header";
import CredentialsFooter from "@/components/footer/Footer";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import callCenter from "@/assets/images/call-center.svg";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { ContactCenterModel } from "@/models/ContactCenterModel";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import FreshStartButton from "@/components/fresh-start-button/fresh-start-button";
import SimpleErrorMessage from "@/components/error-message/new-error-message";
import { FreshStartOrigin } from "@/config/FreshStartOriginConfig";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { FormError } from "@/models/continente-credentials/common/FormError";
import { FreshStartRequest, FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";
import { tryCatch } from "@/utils/try-catch";
import { genericService } from "@/services/GenericService";

type ContactCenterProps = {
  freshStartOrigin?: FreshStartOrigin;
};

export default function ContactCenter(props: ContactCenterProps) {
  const { t } = useTranslation();
  const history = useHistory();

  let previousStepBackXTimes = 1;

  const location = useLocation<ContactCenterModel>();
  const backStep: string | undefined =
    location.state?.properties?.backStep?.substring(4);
  const isPasswordRecover = location.state?.properties?.previousStep === "validate-otp";
  const phoneFreshStartEligible = location.state?.properties?.phoneFreshStartEligible === true;

  const ANCHOR_NEED_HELP = "";

  const digitalAssetContext = useContext(DigitalAssetContext);

  const { handleResponse } = useApiResponseHandler();
  const [loading, setLoading] = useState(false);

  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormError>({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const backStepCardAssociation = () => {
    if (backStep) {
      if (backStep === "/card/continente-card-association") {
        previousStepBackXTimes = 2;
        return true;
      }

      if (backStep === "/loyalty/cards/verify-otp") {
        previousStepBackXTimes = 3;
        return true;
      }
    }
    return false;
  };

  const freshStart = async () => {
    clearErrors();
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse, FreshStartRequest>(
      "api/phonenumber/login/fresh-start",
      {
        origin: props.freshStartOrigin
      }
    ));

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-12 pb-2">
      <div className="justify-center">
        <CredentialsHeader
          title={t("new_contact_center.header_title")}
          subtitle={t("new_contact_center.subtitle")}
          isTextAlignCenter
          showLoading={loading}
          {...(isPasswordRecover
            ? { backClickToPushPath: `/auth/${digitalAssetContext.digitalAsset.clientId}` }
            : backStepCardAssociation()
              ? { previousStepBackXTimes }
              : backStep
                ? { backClickToPushPath: backStep }
                : { previousStepBackXTimes })}
        />
      </div>
      <div className="flex justify-center items-center">
        <img src={callCenter} alt="" />
      </div>
      {errors.submitError?.message && (
        <SimpleErrorMessage
          id="contact_center__error-message"
          error={errors.submitError.message}
        />
      )}
      <div className="flex flex-col gap-4">
        {props.freshStartOrigin && phoneFreshStartEligible && (
          <FreshStartButton onClick={freshStart} />
        )}
        <NeedHelpButton anchor={ANCHOR_NEED_HELP} />
      </div>
      <div className="flex flex-col">
        <CredentialsFooter
          contextualInformationVisible={false}
          footerAppsWebsitesIconsVisible={false}
        />
      </div>
    </div>
  );
}
